import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import type {
  CaseImageVariantListItem,
  UpscaleTargetSize,
} from '@messenger/types'
import { CASE_IMAGE_VARIANT_ERROR_CODE } from '@messenger/consts'
import { pickReusableUpscale, upscaleResampleSize } from '@messenger/utils'
import type { CaseImageTile } from '@/atoms/caseImageAtoms'
import {
  acceptCaseImageVariant,
  getCaseImageVariantStatus,
  processCaseImageVariant,
  setCaseImageVariantReverted,
  type CaseImageVariantStatus,
} from '@/api/caseImageVariants'
import {
  readEnhanceDisclosureDismissed,
  writeEnhanceDisclosureDismissed,
} from '@/utils/caseImages/enhanceStorage'
import {
  ENHANCE_VARIANT_KIND,
  EnhanceGenerationError,
  runEnhanceGeneration,
} from '@/utils/caseImages/runEnhanceGeneration'
import {
  isAiImageEditsDisabledError,
  isRetryableVariantErrorCode,
  mayHaveCommitted,
} from '@/utils/caseImages/variantErrorCodes'
import { getImageDimensions } from '@/utils/editor/getImageDimensions'

/** What an Enhance request carries besides the photo. */
export type EnhanceRequestOptions = {
  /**
   * The smallest pixel size the photo needs where it is placed, sent by the
   * editor's low-resolution warning. Absent from Case Details, where the
   * photo has no placement and the enhancement is the model's own output.
   */
  targetSize?: UpscaleTargetSize
}

/**
 * Whether a finished, unaccepted upscale is big enough to reopen for a
 * request that names a target: true when the server would not grow it any
 * further for that target (it already covers it, or the cap stops there).
 * A result that cannot be measured is reopened, as it always was before
 * targets existed, rather than paying for a generation on a guess.
 */
const pendingUpscaleMeetsTarget = async (
  url: string,
  targetSize: UpscaleTargetSize,
): Promise<boolean> => {
  try {
    const size = await getImageDimensions(url)
    return upscaleResampleSize(size, targetSize) === null
  } catch {
    return true
  }
}

/**
/**
 * Whether the backend refused a write because the photo changed on the
 * server since this screen read it: a 409 (a newer enhancement was accepted,
 * so the one on screen would never show or is no longer the one on show) or
 * a 412 (the enhancement on screen is no longer one that can be reverted).
 * Nothing was written, and the screen is out of date.
 */
const isPhotoChangedRefusal = (err: unknown): boolean =>
  axios.isAxiosError(err) &&
  (err.response?.status === 409 || err.response?.status === 412)

/**
 * Every screen the Enhance panel can show. The panel takes over the upload
 * section for any stage other than `idle`.
 */
export type EnhanceState =
  | { stage: 'idle' }
  | { stage: 'disclosure'; tile: CaseImageTile }
  | {
      stage: 'enhancing'
      tile: CaseImageTile
      status: CaseImageVariantStatus | null
    }
  | {
      stage: 'complete'
      tile: CaseImageTile
      variantId: string
      url: string
      accepting: boolean
    }
  | {
      stage: 'error'
      tile: CaseImageTile
      /** The backend's failure code, or null when it sent none. */
      code: string | null
      /** False when trying again cannot help (cap, spend limit, moderation, not configured). */
      retryable: boolean
    }
  | {
      stage: 'compare'
      tile: CaseImageTile
      /** Which accepted enhancement is on screen; defaults to the newest. */
      variant: CaseImageVariantListItem
    }
  | {
      stage: 'revert'
      tile: CaseImageTile
      /** True while the revert is being saved; both actions wait for it. */
      saving: boolean
    }
  | {
      /**
       * An accept, a revert or its undo was refused because the photo changed
       * on the server since the screen read it (someone accepted a newer
       * enhancement, or reverted or undid one). Nothing was saved or billed,
       * the photo was read again, and the screen says so instead of offering
       * the out-of-date action again.
       */
      stage: 'changed'
      tile: CaseImageTile
    }

type UseEnhanceImageOptions = {
  /** The signed-in user; the disclosure dismissal is remembered per user. */
  userId: string | null | undefined
  /**
   * Called after the photo an image shows changed on the server (an accept,
   * a revert, or undoing a revert), or after such a request failed with no
   * clear answer, so the caller can reload the case's images and variants
   * and move whatever shows the photo to the file the server now prefers.
   */
  onShownImageChanged: (caseImageId: string) => Promise<void> | void
  /**
   * Called after an image's variants changed while the file it shows did
   * not: a generation finished, or its result was discarded. The caller
   * reloads the case's variants, so the tiles it hands back to
   * `requestEnhance` hold the finished result, and the next Enhance of the
   * photo reopens it instead of paying for another generation. A failure is
   * reported through `onError` and changes nothing on screen.
   */
  onVariantsChanged: (caseImageId: string) => Promise<void> | void
  /** Reports unexpected request failures (not the designed error state). */
  onError?: (message: string, error: Error) => void
}

/**
 * How saving a revert, or its undo, ended. `changed` is a refusal because
 * the photo changed on the server since the screen read it.
 */
type RevertOutcome = 'saved' | 'disabled' | 'changed' | 'failed'

const IDLE: EnhanceState = { stage: 'idle' }

/**
 * The error stage for a request refused because AI image edits were switched
 * off since the screen opened: nothing changed, and asking again cannot help.
 */
const disabledError = (tile: CaseImageTile): EnhanceState => ({
  stage: 'error',
  tile,
  code: CASE_IMAGE_VARIANT_ERROR_CODE.AI_IMAGE_EDITS_DISABLED,
  retryable: false,
})

/**
 * Drives the Enhance flow for one image at a time: first-time disclosure,
 * generation with polling, compare-and-accept, the designed error state,
 * plus the follow-up views for an accepted enhancement (full-size compare,
 * revert). The billable accept only ever happens from `accept()`; a revert
 * and its undo are saved on the server for everyone and bill nothing.
 *
 * Two guards keep paid generations from being orphaned: a new request is
 * refused while one is in flight, and an image that already has a finished
 * but unaccepted upscale newer than its newest accepted one reopens that
 * result instead of generating again (`pickReusableUpscale`). Every finished
 * generation reloads the variants (`onVariantsChanged`), so the tiles see it.
 */
export const useEnhanceImage = ({
  userId,
  onShownImageChanged,
  onVariantsChanged,
  onError,
}: UseEnhanceImageOptions) => {
  const [state, setState] = useState<EnhanceState>(IDLE)

  const report = useCallback(
    (err: unknown) => {
      const error = err instanceof Error ? err : new Error(String(err))
      onError?.(error.message, error)
    },
    [onError],
  )

  // Read the photo again after a write that may have landed or was refused
  // as out of date. The reload's own failure leaves the photo as it was; the
  // write's outcome is what the caller reports.
  const reloadShownImageQuietly = useCallback(
    async (caseImageId: string) => {
      try {
        await onShownImageChanged(caseImageId)
      } catch {
        // See above: nothing more to report.
      }
    },
    [onShownImageChanged],
  )

  const reloadVariants = useCallback(
    async (caseImageId: string) => {
      try {
        await onVariantsChanged(caseImageId)
      } catch (err) {
        report(err)
      }
    },
    [onVariantsChanged, report],
  )

  // Each generation gets a run id; bumping it makes the running loop stop
  // at its next check and ignore its own result. Bumped on cancel, on a
  // new run, and on unmount so nothing writes into a dead component.
  const runIdRef = useRef(0)
  // The target of the request now on screen. Set by every requestEnhance
  // (cleared when the request names none), so the disclosure's confirm and a
  // "Try again" generate for the same placement the person started from.
  const targetSizeRef = useRef<UpscaleTargetSize | undefined>(undefined)
  // True from the moment a run starts until it ends or is abandoned. Set
  // synchronously, so two calls before React re-renders (a double-click, or
  // a second request while a finished result is still being measured) see
  // it, which `state.stage` from the last render's closure cannot show.
  const inFlightRef = useRef(false)
  // True while a revert or its undo is being saved. Set synchronously, so a
  // second click before React re-renders sends nothing more.
  const revertSavingRef = useRef(false)
  useEffect(
    () => () => {
      runIdRef.current += 1
      inFlightRef.current = false
    },
    [],
  )

  const startGeneration = useCallback(
    async (tile: CaseImageTile) => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      const targetSize = targetSizeRef.current
      const runId = ++runIdRef.current
      const isCancelled = () => runIdRef.current !== runId
      try {
        // A finished, unaccepted upscale is free to show again: reuse it rather
        // than queue (and later bill) another generation of the same image. Only
        // one newer than the newest accepted upscale counts, since accepting an
        // older one would bill a file that never shows. A request from a
        // placement reuses it only when it is already as large as that
        // placement can get, or accepting it would leave the warning up.
        const pending = pickReusableUpscale(tile.variants)
        const reusable =
          pending?.url &&
          (!targetSize ||
            (await pendingUpscaleMeetsTarget(pending.url, targetSize)))
        if (isCancelled()) return
        if (pending?.url && reusable) {
          setState({
            stage: 'complete',
            tile,
            variantId: pending.id,
            url: pending.url,
            accepting: false,
          })
          return
        }

        setState({ stage: 'enhancing', tile, status: null })

        try {
          const result = await runEnhanceGeneration(tile.image.id, {
            process: (caseImageId) =>
              processCaseImageVariant({
                caseImageId,
                kind: ENHANCE_VARIANT_KIND,
                ...(targetSize ? { targetSize } : {}),
              }),
            getStatus: getCaseImageVariantStatus,
            isCancelled,
            onStatus: (status) => {
              if (isCancelled()) return
              setState((current) =>
                current.stage === 'enhancing' && current.tile === tile
                  ? { ...current, status }
                  : current,
              )
            },
          })
          if (isCancelled()) return
          setState({
            stage: 'complete',
            tile,
            variantId: result.variantId,
            url: result.url,
            accepting: false,
          })
          // The tiles still hold the variants from before this run; reload them
          // so a Discard followed by Enhance reopens this result for free.
          void reloadVariants(tile.image.id)
        } catch (err) {
          if (isCancelled()) return
          if (err instanceof EnhanceGenerationError) {
            if (err.reason === 'cancelled') return
            setState({
              stage: 'error',
              tile,
              code: err.code,
              retryable: isRetryableVariantErrorCode(err.code),
            })
            return
          }
          setState({ stage: 'error', tile, code: null, retryable: true })
        }
      } finally {
        // An abandoned run's guard was already released by whatever
        // abandoned it; a newer run may hold it now.
        if (runIdRef.current === runId) inFlightRef.current = false
      }
    },
    [reloadVariants],
  )

  /**
   * Abandon the run on screen, if any: its loop stops at the next check and
   * its result is ignored, and the in-flight guard is released.
   */
  const abandonRun = useCallback(() => {
    runIdRef.current += 1
    inFlightRef.current = false
  }, [])

  /**
   * Entry point from the tile menu and the low-resolution tooltip. Refused
   * while a generation is running so two paid jobs can never overlap.
   */
  const requestEnhance = useCallback(
    (tile: CaseImageTile, options?: EnhanceRequestOptions) => {
      if (inFlightRef.current) return
      targetSizeRef.current = options?.targetSize
      if (readEnhanceDisclosureDismissed(userId)) {
        void startGeneration(tile)
      } else {
        runIdRef.current += 1
        setState({ stage: 'disclosure', tile })
      }
    },
    [startGeneration, userId],
  )

  const confirmDisclosure = useCallback(() => {
    if (state.stage !== 'disclosure') return
    writeEnhanceDisclosureDismissed(userId)
    void startGeneration(state.tile)
  }, [startGeneration, state, userId])

  /**
   * Leave any stage. Stops a running generation; nothing is billed. Leaving
   * a finished result (Discard changes) reloads the variants as well, in case
   * the reload after the generation did not land, so the result is reopened
   * for free next time.
   */
  const cancel = useCallback(() => {
    abandonRun()
    if (state.stage === 'complete') void reloadVariants(state.tile.image.id)
    setState(IDLE)
  }, [abandonRun, reloadVariants, state])

  const tryAgain = useCallback(() => {
    if (state.stage !== 'error' || !state.retryable) return
    void startGeneration(state.tile)
  }, [startGeneration, state])

  /**
   * The billable action: keep the generated result. The in-flight guard is
   * already released while the accept is pending, so a new request (for
   * another photo, say) may take the screen before it settles; the accept
   * then writes nothing to the screen. It owns the screen only while the
   * run id it read at its start is still current.
   */
  const accept = useCallback(async () => {
    if (state.stage !== 'complete' || state.accepting) return
    const { tile, variantId } = state
    const runId = runIdRef.current
    const ownsScreen = () => runIdRef.current === runId
    setState({ ...state, accepting: true })
    try {
      // The backend keeps a result only when it is newer than every accepted
      // enhancement of the photo (an older one is refused with 409), so a
      // kept result is the newest, carries no revert mark, and shows.
      await acceptCaseImageVariant(variantId)
    } catch (err) {
      if (isAiImageEditsDisabledError(err)) {
        // AI image edits were switched off since this screen opened: the
        // accept was refused before anything was written, so nothing is
        // billed and asking again cannot help. The error stage says so, with
        // no "Try again".
        if (ownsScreen()) setState(disabledError(tile))
        return
      }
      if (isPhotoChangedRefusal(err)) {
        // A newer enhancement of the photo was accepted since this result was
        // offered, so this one would never show: nothing was kept or billed.
        // The photo is read again and the screen says so.
        await reloadShownImageQuietly(tile.image.id)
        if (ownsScreen()) setState({ stage: 'changed', tile })
        return
      }
      report(err)
      if (mayHaveCommitted(err)) {
        // The accept may have been kept, so the photo is read again and the
        // tile shows what the server holds. The accept stays on offer: accepting
        // a variant the backend already keeps changes nothing there, so
        // sending it again cannot count it twice.
        await reloadShownImageQuietly(tile.image.id)
      }
      if (ownsScreen()) setState({ ...state, accepting: false })
      return
    }
    // The accept is kept. A reload that fails is reported, and the screen
    // still closes: there is nothing left to accept.
    try {
      await onShownImageChanged(tile.image.id)
    } catch (err) {
      report(err)
    }
    if (ownsScreen()) setState(IDLE)
  }, [onShownImageChanged, reloadShownImageQuietly, report, state])

  const openCompare = useCallback(
    (tile: CaseImageTile, variant?: CaseImageVariantListItem) => {
      const chosen = variant ?? tile.display.acceptedVariant
      if (!chosen) return
      abandonRun()
      setState({ stage: 'compare', tile, variant: chosen })
    },
    [abandonRun],
  )

  /**
   * Save the revert mark on the photo's newest accepted enhancement
   * (`reverted` true) or clear it (false), then refresh whatever shows the
   * photo. A refusal because AI image edits were switched off comes back as
   * `disabled` for the caller to show. A refusal because the photo changed
   * on the server since the screen read it (409, 412) reads the photo again
   * and comes back as `changed`. Any other failure is reported, and one with
   * no clear answer still refreshes, since the save may have landed. The
   * save and the refresh are separate steps: once the save landed the
   * outcome is `saved`, and a refresh that fails is reported on its own.
   */
  const saveRevert = useCallback(
    async (
      tile: CaseImageTile,
      variantId: string,
      reverted: boolean,
    ): Promise<RevertOutcome> => {
      revertSavingRef.current = true
      try {
        try {
          await setCaseImageVariantReverted(variantId, reverted)
        } catch (err) {
          if (isAiImageEditsDisabledError(err)) return 'disabled'
          if (isPhotoChangedRefusal(err)) {
            await reloadShownImageQuietly(tile.image.id)
            return 'changed'
          }
          report(err)
          if (mayHaveCommitted(err)) {
            await reloadShownImageQuietly(tile.image.id)
          }
          return 'failed'
        }
        try {
          await onShownImageChanged(tile.image.id)
        } catch (err) {
          report(err)
        }
        return 'saved'
      } finally {
        revertSavingRef.current = false
      }
    },
    [onShownImageChanged, reloadShownImageQuietly, report],
  )

  const openRevert = useCallback(
    (tile: CaseImageTile) => {
      abandonRun()
      setState({ stage: 'revert', tile, saving: false })
    },
    [abandonRun],
  )

  /**
   * Show the original instead of the accepted enhancement, for everyone. The
   * screen stays on the confirmation until the save settles; like an accept,
   * it owns the screen only while the run id it read at its start is current.
   */
  const confirmRevert = useCallback(async () => {
    if (state.stage !== 'revert' || state.saving || revertSavingRef.current) {
      return
    }
    const { tile } = state
    const variant = tile.display.acceptedVariant
    if (!variant) {
      setState(IDLE)
      return
    }
    const runId = runIdRef.current
    const ownsScreen = () => runIdRef.current === runId
    setState({ ...state, saving: true })
    const outcome = await saveRevert(tile, variant.id, true)
    if (!ownsScreen()) return
    if (outcome === 'saved') setState(IDLE)
    else if (outcome === 'disabled') setState(disabledError(tile))
    else if (outcome === 'changed') setState({ stage: 'changed', tile })
    else setState({ stage: 'revert', tile, saving: false })
  }, [saveRevert, state])

  /**
   * Undo a revert: show the accepted enhancement again, for everyone, at no
   * charge. Runs straight from the tile menu, so it changes the screen only
   * when the request is refused: because AI image edits are off, or because
   * the photo changed on the server since the menu was drawn.
   */
  const showEnhancement = useCallback(
    async (tile: CaseImageTile) => {
      const variant = tile.display.acceptedVariant
      if (!variant || revertSavingRef.current) return
      const runId = runIdRef.current
      const outcome = await saveRevert(tile, variant.id, false)
      if (runIdRef.current !== runId) return
      if (outcome === 'disabled') setState(disabledError(tile))
      else if (outcome === 'changed') setState({ stage: 'changed', tile })
    },
    [saveRevert],
  )

  const enhancingImageId =
    state.stage === 'enhancing' ? state.tile.image.id : null

  return {
    state,
    enhancingImageId,
    /** True while a generation runs; every tile's Enhance item is disabled. */
    enhanceLocked: state.stage === 'enhancing',
    requestEnhance,
    confirmDisclosure,
    cancel,
    tryAgain,
    accept,
    openCompare,
    openRevert,
    confirmRevert,
    showEnhancement,
  }
}

/** What `useEnhanceImage` returns; the panel and grid receive it as a whole. */
export type EnhanceController = ReturnType<typeof useEnhanceImage>
